import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy, signal, WritableSignal } from '@angular/core';
import { environment } from '@env/environment';
import {
  catchError,
  EMPTY,
  from,
  Observable,
  of,
  Subject,
  SubscriptionLike,
  switchMap,
  takeUntil,
  tap,
  throwError,
} from 'rxjs';
import { map } from 'rxjs/operators';
import { config } from '../../../core/app-config';
import { ApiResponse, SegmentEvent } from '../../interfaces';
import { DeviceDetectionService } from '../device-detection/device-detection.service';
import { EventSourceService } from '../event-source/event-source.service';
import { PosthogService } from '../posthog/posthog.service';
import { SegmentIoService } from '../segmentIo/segment-io.service';
import { ToastNotificationsService } from '../toast-notifications/toast-notifications.service';
import { StreamEvent, StreamEventDataType } from './InboxAI.service';

// Interface for window.webkit.messageHandlers
interface CustomWindow extends Window {
  webkit?: {
    messageHandlers?: {
      permissionHandler?: {
        postMessage: (message: unknown) => void;
      };
      // Other handlers can be added here if needed
    };
  };
  // Android Permission Handler
  permissionHandler?: {
    postMessage: (message: string) => void;
  };
}

export interface TranscriptionSession {
  id: string | null;
  secret: string | null;
  model: string | null;
  expires_at: number | null;
}

export interface VoiceSession {
  id: string;
  type: TranscriptionType;
  resourceId: string | null;

  $transcribedText: WritableSignal<string>;
  $finalText: WritableSignal<string>;
  $state: WritableSignal<TranscriptionState>;

  // WebRTC objects (only during recording)
  peerConnection: RTCPeerConnection | null;
  dataChannel: RTCDataChannel | null;
  mediaStream: MediaStream | null;

  // Session management
  eventSourceSubscription: SubscriptionLike | null;

  // Audio processing tracking
  audioCommitted: boolean;
  waitingForCommit: boolean;
  commitTimeout: ReturnType<typeof setTimeout> | null;
  speechStopped: boolean;

  // Recording duration tracking
  recordingStartTime: number | null;

  // Delta events tracking
  deltaEventsReceived: boolean;

  // Transcription metadata (populated when transcription finishes)
  transcriptionMetadata: {
    model?: string;
    prompt_version?: string;
    prompt_uuid?: string;
    transcription_type?: TranscriptionType;
    resource_id?: string | null;
  } | null;
}

export enum TranscriptionState {
  Idle = 'idle',
  Initializing = 'initializing',
  Recording = 'recording',
  Stopping = 'stopping',
  Finalizing = 'finalizing',
  Editing = 'editing',
  Error = 'error',
  Success = 'success',
}

export enum TranscriptionType {
  Inbox = 'inbox',
  Copilot = 'copilot',
}

@Injectable({
  providedIn: 'root',
})
export class VoiceToTextService implements OnDestroy {
  private debug = !environment.production && true;

  private static readonly TRANSCRIPTION_URL = 'https://api.openai.com/v1/realtime';

  // Timeout constants for audio processing
  private readonly AUDIO_COMMITMENT_TIMEOUT_MS = 3000; // 3 seconds
  private readonly TRANSCRIPTION_GRACE_PERIOD_MS = 1000; // 1 second

  private readonly baseApiUrl = config.API_URL;
  private readonly destroy$ = new Subject<void>();

  // Session management
  private sessions = new Map<string, VoiceSession>();
  private activeSessionId: string | null = null;
  private recordingSessionId: string | null = null; // Track session actually recording audio

  public enabled$: Observable<boolean>;

  isIos = this.deviceDetectionService.isIosApp();
  isAndroid = this.deviceDetectionService.isAndroidApp();

  constructor(
    private http: HttpClient,
    private posthog: PosthogService,
    private eventSourceService: EventSourceService,
    private toast: ToastNotificationsService,
    private deviceDetectionService: DeviceDetectionService,
    private segmentIoService: SegmentIoService
  ) {
    this.enabled$ = of(!this.isAndroid);
  }

  /**
   * Track voice-to-text errors with consistent metadata
   * Safe wrapper - tracking errors will never break the main flow
   */
  private trackVoiceError(
    session: VoiceSession | null,
    errorType: string,
    error: Error | string,
    additionalContext?: Record<string, unknown>
  ): void {
    try {
      const errorMessage = typeof error === 'string' ? error : error.message;

      const metadata = {
        transcription_type: session?.type || 'unknown',
        resource_id: session?.resourceId || null,
        error_type: errorType,
        error_message: errorMessage,
        session_id: session?.id || 'unknown',
        user_agent: navigator.userAgent || 'unknown',
        is_mobile: this.deviceDetectionService.$isMobile?.value ?? false,
        is_ios: this.isIos,
        is_android: this.isAndroid,
        ...additionalContext,
      };

      // Use different event types for microphone vs other errors
      const isMicrophoneError = errorType.startsWith('microphone_');
      const eventType = isMicrophoneError ? SegmentEvent.VoiceToTextMicrophoneError : SegmentEvent.VoiceToTextError;
      const logPrefix = isMicrophoneError ? '🎙️ VoiceToTextMicrophoneError:' : '🚨 VoiceToTextError:';

      this.log(logPrefix, errorType, metadata);
      this.segmentIoService.track(eventType, metadata);
    } catch (trackingError) {
      // Never let tracking errors break the main flow
      console.warn('Failed to track voice error - continuing with main flow:', trackingError);
      // Still log the original error for debugging
      console.error('Original voice error that failed to track:', errorType, error);
    }
  }

  public startRecording(sessionId: string): void {
    const session = this.sessions.get(sessionId);
    if (!session) {
      console.error('Session not found:', sessionId);
      return;
    }

    this.log('🎙️ Button clicked - starting recording for session:', sessionId);

    // Only allow starting from Idle or Success state
    const currentState = session.$state();
    if (currentState !== TranscriptionState.Idle && currentState !== TranscriptionState.Success) {
      this.log('⚠️ Session not in Idle or Success state, ignoring start request. Current state:', currentState);
      return;
    }

    // If starting from Success state, reset to Idle first
    if (currentState === TranscriptionState.Success) {
      this.resetSessionForNewRecording(sessionId);
    }

    // Prevent multiple simultaneous recordings
    if (this.activeSessionId && this.activeSessionId !== sessionId) {
      const activeSession = this.sessions.get(this.activeSessionId);
      if (
        activeSession &&
        (activeSession.$state() === TranscriptionState.Initializing ||
          activeSession.$state() === TranscriptionState.Recording)
      ) {
        this.log('🛑 Stopping active session before starting new recording:', this.activeSessionId);
        this.stopRecording(this.activeSessionId);
      }
    }

    // Stop any currently recording session
    if (this.recordingSessionId && this.recordingSessionId !== sessionId) {
      this.log('🛑 Stopping currently recording session:', this.recordingSessionId);
      this.stopRecording(this.recordingSessionId);
    }

    this.activeSessionId = sessionId;

    // Set recording start time for duration tracking
    session.recordingStartTime = Date.now();

    // Reset delta events tracking for this recording session
    session.deltaEventsReceived = false;

    const buttonPressedMetadata = {
      transcription_type: session.type,
      resource_id: session.resourceId,
    };

    this.log('🎙️ VoiceToTextButtonPressed:', buttonPressedMetadata);
    this.segmentIoService.track(SegmentEvent.VoiceToTextButtonPressed, buttonPressedMetadata);

    // Set to initializing state immediately (hides button)
    session.$state.set(TranscriptionState.Initializing);
    this.log('🔄 State set to Initializing - button should be hidden now');

    this.log('🎙️ Checking microphone permission first...');

    // Check microphone permission before fetching token
    this.checkMicrophonePermission()
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => {
          this.log('✅ Microphone permission granted, fetching token...');
          return this.getTranscriptionSession(session.type, session.resourceId);
        }),
        switchMap((transcriptionSession: TranscriptionSession) => {
          this.log('✅ Token received successfully:', {
            id: transcriptionSession.id,
            model: transcriptionSession.model,
            expires_at: transcriptionSession.expires_at,
          });

          if (!this.isValidTranscriptionSession(transcriptionSession)) {
            throw new Error('Invalid transcription session received');
          }

          // Track when we actually start recording (have permission, token, and about to start listening)
          const startRecordingMetadata = {
            transcription_type: session.type,
            resource_id: session.resourceId,
          };

          this.log('🎙️ VoiceToTextStartedRecording:', startRecordingMetadata);
          this.segmentIoService.track(SegmentEvent.VoiceToTextStartedRecording, startRecordingMetadata);

          this.log('🚀 Starting WebRTC setup with received token...');
          return this.startTranscribing(transcriptionSession, session);
        })
      )
      .subscribe({
        next: () => {
          session.$state.set(TranscriptionState.Recording);
          this.recordingSessionId = session.id;
          this.log('🎤 Recording started successfully - state set to Recording');
        },
        error: (error) => {
          console.error('Error in voice transcription:', error);
          this.log('❌ Error during permission check, token fetch, or WebRTC setup:', error);

          // Check if this is a microphone error that was already handled
          if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
            // Already handled in checkMicrophonePermission, don't track again
            return;
          }

          this.trackVoiceError(session, 'transcription_session_failed', error);

          // Reset to Idle state so user can try again, rather than Error state
          session.$state.set(TranscriptionState.Idle);
          this.log('🔄 Session reset to Idle state - user can retry');

          this.toast.error('Voice to text is currently not working. Please try again later.');
        },
      });
  }

  private startTranscribing(transcriptionSession: TranscriptionSession, session: VoiceSession): Observable<void> {
    return new Observable<void>((observer) => {
      // Reset audio commitment tracking for new recording
      this.resetAudioCommitmentTracking(session);

      // Setup WebRTC infrastructure
      this.setupWebRTCConnection(session);
      this.setupDataChannelHandlers(session);

      // Start the WebRTC flow
      this.setupMediaStream(session)
        .pipe(
          takeUntil(this.destroy$),
          // Check session state before creating offer
          switchMap(() => {
            if (session.$state() === TranscriptionState.Error) {
              this.log('⚠️ Session in error state, stopping WebRTC offer creation');
              return EMPTY;
            }
            return this.createWebRTCOffer(session);
          }),
          // Check session state before establishing connection
          switchMap((localDescription) => {
            if (session.$state() === TranscriptionState.Error) {
              this.log('⚠️ Session in error state, stopping OpenAI connection');
              return EMPTY;
            }
            return this.establishOpenAIConnection(localDescription, transcriptionSession.secret);
          }),
          // Check session state before completing handshake
          switchMap((sdpAnswer) => {
            if (session.$state() === TranscriptionState.Error) {
              this.log('⚠️ Session in error state, stopping WebRTC handshake');
              return EMPTY;
            }
            return this.completeWebRTCHandshake(session, sdpAnswer);
          })
        )
        .subscribe({
          next: () => {
            session.$state.set(TranscriptionState.Recording);
            this.recordingSessionId = session.id; // Track that this session is now recording

            // Notify the observer that recording has started successfully
            observer.next();
            // Don't complete - the Observable should stay active during recording
          },
          error: (error) => {
            console.error('Error in WebRTC setup:', error);
            this.trackVoiceError(session, 'webrtc_setup_failed', error);
            observer.error(error);
          },
        });

      return () => {
        this.stopRecording(session.id);
      };
    });
  }

  /**
   * Reset audio commitment tracking for a new recording session
   */
  private resetAudioCommitmentTracking(session: VoiceSession): void {
    session.audioCommitted = false;
    session.waitingForCommit = false;
    if (session.commitTimeout) {
      clearTimeout(session.commitTimeout);
      session.commitTimeout = null;
    }
  }

  /**
   * Check microphone permission before starting the recording process
   */
  private checkMicrophonePermission(): Observable<void> {
    return from(
      navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: 24000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
        video: false,
      })
    ).pipe(
      tap((stream) => {
        // Immediately stop the stream since we're just checking permission
        stream.getTracks().forEach((track) => track.stop());
      }),
      map(() => void 0), // Convert to void
      catchError((error) => {
        console.error('Microphone permission check failed:', error);

        // Find the session to get context for error tracking
        const session = this.activeSessionId ? this.sessions.get(this.activeSessionId) : null;

        if (session) {
          // Handle the microphone error and track it
          this.handleMicrophonePermissionError(session, error);
        }

        return throwError(() => error);
      })
    );
  }

  /**
   * Handle microphone permission errors during the initial check
   */
  private handleMicrophonePermissionError(session: VoiceSession, error: MediaStreamError): void {
    // Determine error type
    let errorType = 'microphone_generic_error';
    if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
      errorType = 'microphone_permission_denied';
    } else if (error.name === 'NotFoundError') {
      errorType = 'microphone_not_found';
    } else if (error.name === 'NotReadableError') {
      errorType = 'microphone_in_use';
    }

    // Track the error
    this.trackVoiceError(session, errorType, error);

    // For microphone permission denied, reset to idle state so user can retry
    if (errorType === 'microphone_permission_denied') {
      session.$state.set(TranscriptionState.Idle);
      this.log('🔄 Session reset to Idle state for retry');
    } else {
      // For other critical errors, set to error state
      session.$state.set(TranscriptionState.Error);
    }

    // Clear the recording session tracker
    if (this.recordingSessionId === session.id) {
      this.recordingSessionId = null;
    }

    // Show appropriate error message
    this.showMicrophoneError(error);
  }

  /**
   * Setup microphone media stream with optimized audio constraints
   */
  private setupMediaStream(session: VoiceSession): Observable<MediaStream> {
    return from(
      navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: 24000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
        video: false,
      })
    ).pipe(
      tap((stream) => {
        session.mediaStream = stream;

        // Add audio tracks to peer connection for WebRTC transmission
        stream.getAudioTracks().forEach((track) => {
          if (session.peerConnection) {
            session.peerConnection.addTrack(track, stream);
          }
        });
      }),
      catchError((error) => {
        console.error('Error accessing microphone during WebRTC setup:', error);

        // This should be rare since we already checked permission, but handle it
        this.trackVoiceError(session, 'microphone_webrtc_setup_failed', error);
        session.$state.set(TranscriptionState.Error);

        return throwError(() => new Error('Failed to access microphone during WebRTC setup'));
      })
    );
  }

  /**
   * Create WebRTC offer and set local description
   */
  private createWebRTCOffer(session: VoiceSession): Observable<RTCSessionDescriptionInit> {
    return from(session.peerConnection?.createOffer() || Promise.reject('No peer connection')).pipe(
      catchError((error) => {
        console.error('Error creating offer:', error);
        this.trackVoiceError(session, 'webrtc_offer_creation_failed', error);
        this.showGenericVoiceError();
        return throwError(() => new Error('Failed to create offer'));
      }),
      switchMap((offer) => {
        if (!offer || !session.peerConnection) {
          return throwError(() => new Error('No peer connection or offer'));
        }

        return from(session.peerConnection.setLocalDescription(offer)).pipe(
          map(() => {
            const localDescription = session.peerConnection?.localDescription;
            if (!localDescription) {
              throw new Error('No local description after setting');
            }
            return localDescription;
          })
        );
      }),
      catchError((error) => {
        console.error('Error setting local description:', error);
        this.trackVoiceError(session, 'webrtc_local_description_failed', error);
        this.showGenericVoiceError();
        return throwError(() => new Error('Failed to set local description'));
      })
    );
  }

  /**
   * Establish connection with OpenAI Realtime API using SDP offer
   */
  private establishOpenAIConnection(localDescription: RTCSessionDescriptionInit, secret: string): Observable<string> {
    return from(
      fetch(VoiceToTextService.TRANSCRIPTION_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/sdp',
          Authorization: `Bearer ${secret}`,
        },
        body: localDescription.sdp,
      }).then(async (response) => {
        if (!response.ok) {
          const errorText = await response.text().catch(() => 'Failed to read response text');
          console.error('OpenAI Error Response:', response.status, errorText);
          throw new Error(`HTTP error! Status: ${response.status}, Body: ${errorText}`);
        }
        return response.text();
      })
    ).pipe(
      catchError((error) => {
        console.error('Error establishing WebRTC connection:', error);

        // Determine error type based on fetch response
        let errorType = 'openai_connection_failed';
        let errorCode: number | undefined;

        if (error.message.includes('401') || error.message.includes('403')) {
          errorType = 'openai_authentication_failed';
          errorCode = error.message.includes('401') ? 401 : 403;
        } else if (error.message.includes('429')) {
          errorType = 'openai_rate_limited';
          errorCode = 429;
        } else if (error.message.includes('400')) {
          errorType = 'openai_bad_request';
          errorCode = 400;
        } else if (error.message.includes('5')) {
          errorType = 'openai_server_error';
          errorCode = parseInt(error.message.match(/\d{3}/)?.[0] || '500');
        }

        this.trackVoiceError(this.activeSessionId ? this.sessions.get(this.activeSessionId) : null, errorType, error, {
          error_code: errorCode,
        });
        this.showGenericVoiceError();
        return throwError(() => new Error('Failed to establish WebRTC connection'));
      })
    );
  }

  /**
   * Complete WebRTC handshake by setting remote description from OpenAI
   */
  private completeWebRTCHandshake(session: VoiceSession, sdpAnswer: string): Observable<void> {
    const answer = new RTCSessionDescription({
      type: 'answer',
      sdp: sdpAnswer,
    });

    if (!session.peerConnection) {
      return throwError(() => new Error('No peer connection'));
    }

    return from(session.peerConnection.setRemoteDescription(answer)).pipe(
      catchError((error) => {
        console.error('Error setting remote description:', error);
        this.trackVoiceError(session, 'webrtc_remote_description_failed', error);
        return throwError(() => new Error('Failed to set remote description'));
      })
    );
  }

  private finishRecording(sessionId: string): void {
    this.log('🎤 Finishing recording');

    const session = this.sessions.get(sessionId);
    if (!session) return;

    // Only proceed if this session is actually recording
    const currentState = session.$state();
    if (currentState !== TranscriptionState.Recording && currentState !== TranscriptionState.Stopping) {
      this.log('⚠️ Session is not recording, ignoring stop request. Current state:', currentState);
      return;
    }

    // Clear the recording session tracker
    if (this.recordingSessionId === sessionId) {
      this.recordingSessionId = null;
    }

    // Start finalizing state - grace period to listen to any transcription we haven't processed yet
    session.$state.set(TranscriptionState.Finalizing);

    // Stop audio input but keep data channel open
    this.stopAudioInput(session);

    // Send manual commit message to force OpenAI to process the audio buffer
    this.sendDataChannelMessage(session, { type: 'input_audio_buffer.commit' });

    // Check if audio was already committed
    if (session.audioCommitted) {
      this.log('✅ Audio already committed - starting transcription grace period');
      this.waitForTranscriptionCompletion(session);
    } else {
      this.log('⏳ Waiting for audio commitment before finalizing');
      session.waitingForCommit = true;

      // Start timeout for audio commitment
      session.commitTimeout = setTimeout(() => {
        if (session.waitingForCommit) {
          console.warn('⚠️ No audio commitment received - assuming no usable input');
          this.trackVoiceError(session, 'audio_commitment_timeout', 'No audio commitment received within timeout');
          session.waitingForCommit = false;
          this.finalizeRecording(session);
        }
      }, this.AUDIO_COMMITMENT_TIMEOUT_MS);
    }
  }

  public stopRecording(sessionId: string): void {
    this.log('🛑 Stopping recording');

    const session = this.sessions.get(sessionId);
    if (!session) return;

    // Start finalizing state - grace period to listen to any transcription we haven't processed yet
    session.$state.set(TranscriptionState.Stopping);

    // Calculate recording duration
    const recordingDurationMs = session.recordingStartTime ? Date.now() - session.recordingStartTime : 0;
    const recordingDurationSeconds = Math.round(recordingDurationMs / 1000);

    // Only track stopped recording if session didn't fail due to critical errors
    if (session.$state() !== TranscriptionState.Error || this.shouldTrackEvents(session)) {
      const stopMetadata = {
        transcription_type: session.type,
        resource_id: session.resourceId,
        recording_duration_seconds: recordingDurationSeconds,
        delta_events_received: session.deltaEventsReceived,
      };

      this.log('🛑 VoiceToTextStoppedRecording:', stopMetadata);
      this.segmentIoService.track(SegmentEvent.VoiceToTextStoppedRecording, stopMetadata);
    } else {
      this.log('⚠️ Skipping VoiceToTextStoppedRecording due to critical error');
    }

    setTimeout(() => {
      this.finishRecording(sessionId);
    }, 1250);
  }

  /**
   * Determine if events should still be tracked despite being in error state
   * (for non-critical errors we still want to track the flow)
   */
  private shouldTrackEvents(session: VoiceSession): boolean {
    // If we received delta events, it means recording actually worked
    // and the error happened later in the flow
    return session.deltaEventsReceived || session.recordingStartTime !== null;
  }

  private stopAudioInput(session: VoiceSession): void {
    if (session.mediaStream) {
      session.mediaStream.getTracks().forEach((track) => track.stop());
      session.mediaStream = null;
    }
  }

  /**
   * Send a JSON message through the WebRTC data channel to OpenAI
   */
  private sendDataChannelMessage(session: VoiceSession, message: Record<string, unknown>): void {
    this.log('message', message);
    if (!session.dataChannel) {
      console.error('❌ No data channel available to send message');
      return;
    }

    if (session.dataChannel.readyState !== 'open') {
      console.error('❌ Data channel not open, state:', session.dataChannel.readyState);
      return;
    }

    try {
      const messageJson = JSON.stringify(message);
      session.dataChannel.send(messageJson);
    } catch (error) {
      console.error('❌ Failed to send data channel message:', error);
    }
  }

  private waitForTranscriptionCompletion(session: VoiceSession): void {
    let graceTimer: ReturnType<typeof setTimeout>;
    let isCompleted = false;

    const startGraceTimer = () => {
      if (graceTimer) {
        clearTimeout(graceTimer);
      }

      graceTimer = setTimeout(() => {
        this.log('✅ Grace period expired - finalizing transcription');
        complete();
      }, this.TRANSCRIPTION_GRACE_PERIOD_MS);
    };

    const complete = () => {
      if (isCompleted) return;
      isCompleted = true;
      if (graceTimer) {
        clearTimeout(graceTimer);
      }
      this.finalizeRecording(session);
    };

    // Set up completion listeners in data channel
    if (session.dataChannel) {
      const originalOnMessage = session.dataChannel.onmessage;
      session.dataChannel.onmessage = (event) => {
        // Call original handler first to process transcription
        if (originalOnMessage) originalOnMessage.call(session.dataChannel, event);

        // Check for completion events
        try {
          const data = JSON.parse(event.data);

          // Reset timer for any transcription activity (delta or completed)
          if (data.type === 'conversation.item.input_audio_transcription.delta') {
            startGraceTimer();
          } else if (data.type === 'conversation.item.input_audio_transcription.completed') {
            startGraceTimer();
          }
        } catch {
          // Ignore parse errors
        }
      };
    }

    // Start the grace timer immediately
    startGraceTimer();

    // If we already have transcribed text, we might have missed the completion event
    if (session.$transcribedText().trim().length > 0) {
      this.log('📝 Already have transcribed text - using shorter grace period');
      // Use a shorter grace period since we already have text
      if (graceTimer) {
        clearTimeout(graceTimer);
      }
      graceTimer = setTimeout(() => {
        this.log('✅ Grace period expired (with existing text) - finalizing transcription');
        complete();
      }, 500); // 500ms when we already have text
    }
  }

  private finalizeRecording(session: VoiceSession): void {
    this.log('🎙️ Finalizing recording and starting cleanup');
    session.$state.set(TranscriptionState.Editing);

    // Clear recording session tracking if this was the recording session
    if (this.recordingSessionId === session.id) {
      this.recordingSessionId = null;
    }

    if (session.commitTimeout) {
      clearTimeout(session.commitTimeout);
      session.commitTimeout = null;
    }

    this.cleanupWebRTCHandlers(session);
    this.cleanupMediaResources(session);

    const originalText = session.$transcribedText();

    this.log('originalText', originalText);

    if (originalText.trim().length > 0) {
      // Only track finalized transcription if session didn't fail due to critical errors
      if (session.$state() !== TranscriptionState.Error || this.shouldTrackEvents(session)) {
        const finalizeMetadata = {
          transcription_type: session.type,
          resource_id: session.resourceId,
          transcription_length: originalText.trim().length,
          delta_events_received: session.deltaEventsReceived,
        };

        this.log('✅ VoiceToTextTranscriptionFinalized:', finalizeMetadata);
        this.segmentIoService.track(SegmentEvent.VoiceToTextTranscriptionFinalized, finalizeMetadata);
      } else {
        this.log('⚠️ Skipping VoiceToTextTranscriptionFinalized due to critical error');
      }

      const resourceId = session.resourceId;

      this.streamCleanUpTranscription(session.id, resourceId)
        .pipe(
          takeUntil(this.destroy$),
          catchError(() => {
            session.$finalText.set(originalText);
            session.$state.set(TranscriptionState.Success);
            return throwError(() => new Error('Stream cleanup failed'));
          })
        )
        .subscribe({
          next: () => {
            // Text is updated incrementally in the streaming method
          },
          complete: () => {
            session.$state.set(TranscriptionState.Success);
          },
          error: (streamError) => {
            this.log('❌ Streaming failed, falling back to original transcription:', streamError);

            // Show toast to inform user about fallback
            this.toast.error('Voice processing failed, but your transcription was saved.');

            // Fallback to original transcription
            session.$finalText.set(originalText);
            session.$state.set(TranscriptionState.Success);
          },
        });
    } else {
      // Only track empty transcription if session didn't fail due to critical errors
      if (session.$state() !== TranscriptionState.Error || this.shouldTrackEvents(session)) {
        const emptyMetadata = {
          transcription_type: session.type,
          resource_id: session.resourceId,
          recording_duration_seconds: session.recordingStartTime
            ? Math.round((Date.now() - session.recordingStartTime) / 1000)
            : 0,
          delta_events_received: session.deltaEventsReceived,
        };

        this.log('⚠️ VoiceToTextEmptyTranscription:', emptyMetadata);
        this.segmentIoService.track(SegmentEvent.VoiceToTextEmptyTranscription, emptyMetadata);
      } else {
        this.log('⚠️ Skipping VoiceToTextEmptyTranscription due to critical error');
      }

      session.$finalText.set('');
      session.$state.set(TranscriptionState.Success);
    }
  }

  private isValidTranscriptionSession(session: TranscriptionSession | null): boolean {
    return !!(session?.id && session?.secret && session?.model);
  }

  private getTranscriptionSession(
    transcriptionType: TranscriptionType,
    resourceId: string | null
  ): Observable<TranscriptionSession | null> {
    const url = `${this.baseApiUrl}/start-transcription`;
    return this.http
      .post<ApiResponse<TranscriptionSession | null>>(url, {
        type: transcriptionType,
        resource_id: resourceId,
      })
      .pipe(
        map((res) => res.data),
        tap((session) => {
          if (!this.isValidTranscriptionSession(session)) {
            console.warn('❌ Received invalid or null transcription session');
            this.trackVoiceError(null, 'session_creation_invalid', 'Received invalid transcription session', {
              transcription_type: transcriptionType,
              resource_id: resourceId,
            });
          }
        })
      );
  }

  private streamCleanUpTranscription(sessionId: string, resourceId: string | null): Observable<string> {
    this.log('streamCleanUpTranscription', sessionId, resourceId);
    const session = this.sessions.get(sessionId);
    if (!session) {
      return throwError(() => new Error('Session not found'));
    }

    return this.authenticateForEdit(sessionId, resourceId).pipe(
      switchMap((url) => {
        // Reset final text at the start of streaming
        session.$finalText.set('');

        const editMetadata = {
          transcription_type: session.type,
          resource_id: resourceId,
          original_transcription_length: session.$transcribedText().trim().length,
        };

        this.log('✏️ VoiceToTextEditTranscription:', editMetadata);
        this.segmentIoService.track(SegmentEvent.VoiceToTextEditTranscription, editMetadata);

        this.log('authenticated for edit', url, sessionId, resourceId);
        return new Observable<string>((observer) => {
          session.eventSourceSubscription = this.eventSourceService
            .connectToServerSentEvents(url, {}, ['update'])
            .subscribe({
              next: (event) => {
                this.log('event', event);

                if (event.data === '</stream>') {
                  observer.complete();
                  return;
                }

                try {
                  const data: StreamEvent = JSON.parse(event.data);

                  if (data.type === StreamEventDataType.UPDATE) {
                    session.$finalText.update((currentText) => {
                      return currentText + data.content.reply;
                    });

                    observer.next(data.content.reply);
                    return;
                  }

                  if (data.type === StreamEventDataType.FINISH) {
                    // Track transcription generation with rich metadata
                    const metadata = {
                      model: data.content.model,
                      prompt_version: data.content.prompt_version,
                      prompt_uuid: data.content.uuid,
                      transcription_type: session.type,
                      resource_id: session.resourceId,
                    };

                    // Store complete metadata in session for future use
                    session.transcriptionMetadata = metadata;

                    this.log('🎯 VoiceToTextGenerated:', metadata);
                    this.segmentIoService.track(SegmentEvent.VoiceToTextGenerated, metadata);

                    observer.complete();
                  }

                  if (data.type === StreamEventDataType.ERROR) {
                    // Reset final text on error
                    session.$finalText.set('');
                    observer.next('');
                    return;
                  }

                  if (data.type === StreamEventDataType.FAIL) {
                    this.trackVoiceError(session, 'stream_processing_failed', 'Stream processing failed');
                    observer.error(new Error('Stream failed'));
                    return;
                  }
                } catch {
                  this.trackVoiceError(session, 'stream_json_parse_error', 'Failed to parse stream JSON');
                  observer.error(new Error('Failed to parse JSON'));
                }
              },
              error: (error) => {
                console.error('EventSource error:', error);
                this.trackVoiceError(session, 'stream_connection_failed', error);
                observer.error(error);
              },
            });

          return () => {
            this.closeStream(sessionId);
          };
        });
      }),
      catchError((error) => {
        console.error('Error in streamCleanUpTranscription:', error);
        this.trackVoiceError(session, 'stream_authentication_failed', error);
        return throwError(() => error);
      })
    );
  }

  private closeStream(sessionId: string): void {
    const session = this.sessions.get(sessionId);
    if (!session) return;

    if (session.eventSourceSubscription) {
      session.eventSourceSubscription.unsubscribe();
      session.eventSourceSubscription = null;
      this.eventSourceService.close();
    }
  }

  private authenticateForEdit(sessionId: string, resourceId: string | null): Observable<string> {
    const session = this.sessions.get(sessionId);
    if (!session) {
      return throwError(() => new Error('Session not found'));
    }

    const requestData = {
      route_type: 'edit-transcription',
      resource_id: resourceId,
      message: session.$transcribedText(),
      type: session.type,
    };

    return this.http
      .post<ApiResponse<{ url: string }>>(`${this.baseApiUrl}/authenticate-stream`, requestData)
      .pipe(map((res) => res.data.url));
  }

  ngOnDestroy(): void {
    for (const [sessionId, session] of this.sessions) {
      // Stop recording if in progress
      if (this.isWorking(sessionId) || this.isInitializing(sessionId)) {
        this.stopRecording(sessionId);
      }

      // Clean up session resources
      this.cleanupSession(session);
    }

    // Clear sessions map and tracking
    this.sessions.clear();
    this.activeSessionId = null;
    this.recordingSessionId = null;

    // Complete destroy subject
    this.destroy$.next();
    this.destroy$.complete();
  }

  clearText(sessionId: string): void {
    const session = this.sessions.get(sessionId);
    if (!session) return;

    // Clear text first
    session.$transcribedText.set('');
    session.$finalText.set('');

    // Then reset session to idle state so user can record again
    session.$state.set(TranscriptionState.Idle);
  }

  /**
   * Reset session to idle state for a new recording without clearing text
   */
  public resetSessionForNewRecording(sessionId: string): void {
    const session = this.sessions.get(sessionId);
    if (!session) return;

    // Only reset if session is in a completed state
    const state = session.$state();
    if (state === TranscriptionState.Success || state === TranscriptionState.Error) {
      session.$state.set(TranscriptionState.Idle);

      // Reset transcription metadata for new recording
      session.transcriptionMetadata = null;
      session.deltaEventsReceived = false;
      session.recordingStartTime = null;
    }
  }

  /**
   * Get the current media stream for waveform visualization
   */
  public getCurrentMediaStream(sessionId: string): MediaStream | null {
    const session = this.sessions.get(sessionId);
    if (!session) return null;
    return session.mediaStream;
  }

  /**
   * Sets up WebRTC peer connection for audio streaming to OpenAI
   */
  private setupWebRTCConnection(session: VoiceSession): void {
    session.peerConnection = new RTCPeerConnection();

    session.peerConnection.onconnectionstatechange = () => {
      if (
        session.peerConnection?.connectionState === 'failed' ||
        session.peerConnection?.connectionState === 'disconnected'
      ) {
        console.error('PeerConnection failed or disconnected during recording');
      } else if (session.peerConnection?.connectionState === 'connected') {
        // this.log('📡 WebRTC connection established - ready to stream audio');
      }
    };
  }

  /**
   * Sets up data channel for receiving transcription responses from OpenAI
   */
  private setupDataChannelHandlers(session: VoiceSession): void {
    if (!session.peerConnection) return;

    session.dataChannel = session.peerConnection.createDataChannel('transcription', {
      ordered: true,
      maxRetransmits: 0,
      protocol: '',
    });

    session.dataChannel.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        this.log('🎧 Parsed OpenAI message:', data);

        if (data.type === 'input_audio_buffer.speech_started') {
          this.log('🗣️ Speech detected');
        }

        if (data.type === 'input_audio_buffer.committed') {
          session.audioCommitted = true;
          this.log('📦 Audio committed - ready for transcription');

          // If we were waiting for commitment and user already stopped recording
          if (session.waitingForCommit) {
            session.waitingForCommit = false;
            if (session.commitTimeout) {
              clearTimeout(session.commitTimeout);
              session.commitTimeout = null;
            }
            this.waitForTranscriptionCompletion(session);
          }
        }

        if (data.type === 'conversation.item.input_audio_transcription.delta') {
          this.log('🎤 Voice delta received:', data.delta);
          // Track that we received at least one delta event
          session.deltaEventsReceived = true;
          session.speechStopped = false;
        } else if (data.type === 'conversation.item.input_audio_transcription.completed') {
          this.log('✅ Transcription completed');
          session.$transcribedText.update((currentText) => {
            const newText = currentText + data.transcript;
            this.log('🎤 Updated total transcribed text:', newText);
            return newText;
          });
        } else if (data.type === 'input_audio_buffer.speech_stopped') {
          this.log('🛑 Speech stopped');
          session.speechStopped = true;
        } else if (data.type === 'conversation.item.created') {
          this.log('💬 Conversation item created');
        }
      } catch (error) {
        console.error('Error processing transcription data:', error);
      }
    };

    session.dataChannel.onerror = (event: RTCErrorEvent) => {
      console.error('Data channel error:', event.error?.message || 'Unknown error');
    };
  }

  /**
   * Cleans up WebRTC event handlers to prevent memory leaks
   */
  private cleanupWebRTCHandlers(session: VoiceSession): void {
    if (session.peerConnection) {
      session.peerConnection.onconnectionstatechange = null;
    }

    if (session.dataChannel) {
      session.dataChannel.onmessage = null;
      session.dataChannel.onerror = null;
    }
  }

  /**
   * Batched cleanup of media resources to prevent multiple individual operations
   */
  private cleanupMediaResources(session: VoiceSession): void {
    if (session.mediaStream) {
      session.mediaStream.getTracks().forEach((track) => track.stop());
      session.mediaStream = null;
    }

    if (session.dataChannel) {
      session.dataChannel.close();
      session.dataChannel = null;
    }

    if (session.peerConnection) {
      session.peerConnection.close();
      session.peerConnection = null;
    }
  }

  // Session management
  /**
   * Create a new voice session without pre-initializing tokens
   */
  public createEmptySession(type: TranscriptionType, resourceId: string | null): string {
    const sessionId = `${type}-${resourceId || 'null'}-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;

    const session: VoiceSession = {
      id: sessionId,
      type,
      resourceId,

      $transcribedText: signal<string>(''),
      $finalText: signal<string>(''),
      $state: signal<TranscriptionState>(TranscriptionState.Idle),

      peerConnection: null,
      dataChannel: null,
      mediaStream: null,

      eventSourceSubscription: null,

      audioCommitted: false,
      waitingForCommit: false,
      commitTimeout: null,
      speechStopped: false,

      // Recording duration tracking
      recordingStartTime: null,

      // Delta events tracking
      deltaEventsReceived: false,

      // Transcription metadata
      transcriptionMetadata: null,
    };

    this.sessions.set(sessionId, session);

    return sessionId;
  }

  public destroySession(sessionId: string): void {
    const session = this.sessions.get(sessionId);
    if (!session) return;

    // Stop recording if this session is active
    if (this.activeSessionId === sessionId) {
      this.stopRecording(sessionId);
      this.activeSessionId = null;
    }

    // Clear recording session tracking if this was the recording session
    if (this.recordingSessionId === sessionId) {
      this.recordingSessionId = null;
    }

    this.cleanupSession(session);
    this.sessions.delete(sessionId);
  }

  private cleanupSession(session: VoiceSession): void {
    if (session.eventSourceSubscription) {
      session.eventSourceSubscription.unsubscribe();
      session.eventSourceSubscription = null;
    }

    if (session.commitTimeout) {
      clearTimeout(session.commitTimeout);
      session.commitTimeout = null;
    }

    // Use batched cleanup for media resources
    this.cleanupMediaResources(session);

    // Reset commitment tracking
    session.audioCommitted = false;
    session.waitingForCommit = false;
  }

  // Session-aware public API methods
  public isLoading(sessionId: string): boolean {
    const session = this.sessions.get(sessionId);
    if (!session) return false;

    const state = session.$state();
    return (
      state === TranscriptionState.Initializing ||
      state === TranscriptionState.Editing ||
      state === TranscriptionState.Stopping ||
      state === TranscriptionState.Finalizing
    );
  }

  public isInitializing(sessionId: string): boolean {
    const session = this.sessions.get(sessionId);
    if (!session) return false;
    return session.$state() === TranscriptionState.Initializing;
  }

  public isFinalizing(sessionId: string): boolean {
    const session = this.sessions.get(sessionId);
    if (!session) return false;
    return session.$state() === TranscriptionState.Finalizing;
  }

  public isEditing(sessionId: string): boolean {
    const session = this.sessions.get(sessionId);
    if (!session) return false;
    return session.$state() === TranscriptionState.Editing;
  }

  public wasRecorded(sessionId: string): boolean {
    const session = this.sessions.get(sessionId);
    if (!session) return false;

    // Return true if session completed successfully OR if there's still text present
    const state = session.$state();
    const hasText = session.$finalText().trim().length > 0 || session.$transcribedText().trim().length > 0;

    return state === TranscriptionState.Success || (state === TranscriptionState.Idle && hasText);
  }

  public isWorking(sessionId: string): boolean {
    const session = this.sessions.get(sessionId);
    if (!session) return false;
    return (
      session.$state() === TranscriptionState.Initializing ||
      session.$state() === TranscriptionState.Recording ||
      session.$state() === TranscriptionState.Editing ||
      session.$state() === TranscriptionState.Stopping ||
      session.$state() === TranscriptionState.Finalizing
    );
  }

  public isRecording(sessionId: string): boolean {
    const session = this.sessions.get(sessionId);
    if (!session) return false;
    return session.$state() === TranscriptionState.Recording;
  }

  public isError(sessionId: string): boolean {
    const session = this.sessions.get(sessionId);
    if (!session) return false;
    return session.$state() === TranscriptionState.Error;
  }

  public isIdle(sessionId: string): boolean {
    const session = this.sessions.get(sessionId);
    if (!session) return false;
    return session.$state() === TranscriptionState.Idle;
  }

  public shouldShowButton(sessionId: string): boolean {
    const session = this.sessions.get(sessionId);
    if (!session) return false;

    // Show button when idle or when recording completed successfully
    const state = session.$state();
    return state === TranscriptionState.Idle || state === TranscriptionState.Success;
  }

  public $finalText(sessionId: string): string {
    const session = this.sessions.get(sessionId);
    if (!session) return '';
    return session.$finalText();
  }

  public $currentTranscription(sessionId: string): string {
    const session = this.sessions.get(sessionId);
    if (!session) return '';
    return session.$transcribedText();
  }

  public getTranscriptionMetadata(sessionId: string): {
    model?: string;
    prompt_version?: string;
    prompt_uuid?: string;
    transcription_type?: TranscriptionType;
    resource_id?: string | null;
  } | null {
    const session = this.sessions.get(sessionId);
    return session?.transcriptionMetadata || null;
  }

  private showGenericVoiceError(): void {
    this.toast.error(
      'Something went wrong with voice recording. Please try again later or contact support if the problem persists.'
    );
  }

  private showVoiceErrorToast(message: string): void {
    this.toast.error(message);
  }

  private showMicrophoneError(error: MediaStreamError): void {
    const win = window as CustomWindow;

    if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
      this.showVoiceErrorToast(
        'Voice recording needs microphone access, but you previously declined this permission. You can enable it by clicking the microphone icon in your browser\'s address bar and choosing "Allow"'
      );
    } else if (error.name === 'NotFoundError') {
      this.showVoiceErrorToast('No microphone found. Please check your microphone connection and try again.');
    } else if (error.name === 'NotReadableError') {
      this.showVoiceErrorToast(
        'Microphone is already in use by another application. Please close other apps and try again.'
      );
    } else {
      this.showVoiceErrorToast('Unable to access microphone. Please check your settings and try again.');
    }

    // Send webkit message for iOS apps
    if (this.isIos && win.webkit?.messageHandlers?.permissionHandler) {
      try {
        win.webkit.messageHandlers.permissionHandler.postMessage({
          type: 'permission_denied',
          permission: 'microphone',
          customMessage: null,
        });
      } catch (e) {
        console.error('Error posting message to permissionHandler:', e);
      }
    } else if (this.isIos) {
      console.warn('WebKit message handler permissionHandler not available on iOS.');
    }

    // Send Android interface message for Android apps
    if (this.isAndroid && win.permissionHandler?.postMessage) {
      try {
        win.permissionHandler.postMessage(
          JSON.stringify({
            type: 'permission_denied',
            permission: 'microphone',
            customMessage: null,
          })
        );
      } catch (e) {
        console.error('Error calling Android permissionHandler:', e);
      }
    } else if (this.isAndroid) {
      console.warn('Android permissionHandler not available on Android.');
    }
  }

  private log(...args: unknown[]): void {
    if (!this.debug) {
      return;
    }

    // Use console.log for debugging when debug flag is enabled
    // eslint-disable-next-line no-console
    console.log(...args);
  }
}

// Add interface for MediaStream errors
interface MediaStreamError extends Error {
  name: 'NotAllowedError' | 'PermissionDeniedError' | 'NotFoundError' | 'NotReadableError' | string;
}
